// Dedicated Fan edit form — fan/customer fields only. No performer body/legal
// fields, no shoots, no studio fields, no cast/collab language. Uses the same
// shared /api/crm/ Person record, but the editable surface is fan-specific.
import React from "react";
import { X as XIcon } from "lucide-react";
import { STATUS_HEX, STATUS_LABEL, STATUSES, getRecordStatuses, primaryStatus, toggleRecordStatus } from "./constants";

const FAN_SOURCES = ["Form", "Organic", "Campaign", "Referral", "Other"];

export default function FanForm({ editing, form, setForm, onClose, onSave }) {
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const statuses = getRecordStatuses(form);
  const setStatuses = (next) => setForm((current) => ({
    ...current,
    statuses: next,
    status: primaryStatus(next),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="fan-form">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border"
        style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            <div className="overline" style={{ color: "var(--gio-primary)" }}>{editing === "new" ? "NEW FAN" : "EDIT FAN"}</div>
            <h2 className="text-lg font-bold" style={{ color: "var(--gio-text)" }}>Fan / customer record</h2>
          </div>
          <button onClick={onClose} data-testid="fan-form-close" className="text-zinc-500 hover:text-white"><XIcon size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Fan name / handle *">
            <input data-testid="fan-f-name" value={form.stage_name} onChange={(e) => set("stage_name", e.target.value)}
              placeholder="Display name or handle" className="gio-input w-full" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Status">
              <div className="flex flex-wrap gap-2" role="group" aria-label="Fan statuses" data-testid="fan-f-status">
                {STATUSES.map((status) => {
                  const selected = statuses.includes(status);
                  const color = STATUS_HEX[status] || "#f472b6";
                  return <button key={status} type="button" aria-pressed={selected} data-testid={`fan-f-status-${status}`}
                    onClick={() => setStatuses(toggleRecordStatus(statuses, status))}
                    className="rounded-full border px-2.5 py-1.5 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-primary)]"
                    style={{ borderColor: selected ? `${color}bb` : "var(--gio-border)", color: selected ? color : "var(--gio-muted)", background: selected ? `${color}20` : "var(--gio-surface-2)" }}>
                    {STATUS_LABEL[status] || status.replace(/_/g, " ")}
                  </button>;
                })}
              </div>
            </Field>
            <Field label="How they were acquired">
              <select data-testid="fan-f-source" value={form.source} onChange={(e) => set("source", e.target.value)} className="gio-input w-full">
                {FAN_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Email"><input data-testid="fan-f-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="gio-input w-full" /></Field>
            <Field label="Phone"><input data-testid="fan-f-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="gio-input w-full" /></Field>
          </div>

          <div>
            <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>Platform handles</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="OnlyFans"><input data-testid="fan-f-of" value={form.onlyfans} onChange={(e) => set("onlyfans", e.target.value)} className="gio-input w-full" /></Field>
              <Field label="JustForFans"><input data-testid="fan-f-jff" value={form.justforfans} onChange={(e) => set("justforfans", e.target.value)} className="gio-input w-full" /></Field>
              <Field label="X / Twitter"><input data-testid="fan-f-x" value={form.twitter} onChange={(e) => set("twitter", e.target.value)} className="gio-input w-full" /></Field>
              <Field label="Instagram"><input data-testid="fan-f-ig" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="gio-input w-full" /></Field>
            </div>
            <div className="text-[11px] mt-1.5" style={{ color: "var(--gio-muted-3)" }}>
              A handle here means it is linked on this record. Platform-verified/subscription status is not available from the current data contract.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Country"><input data-testid="fan-f-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="gio-input w-full" /></Field>
            <Field label="City"><input data-testid="fan-f-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="gio-input w-full" /></Field>
          </div>

          <Field label="Segments / tags (comma separated)">
            <input data-testid="fan-f-tags" value={(form.tags || []).join(", ")}
              onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
              placeholder="whale, active_subs, ppv_buyer..." className="gio-input w-full" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Preferences / kinks"><textarea data-testid="fan-f-kinks" value={form.kinks} onChange={(e) => set("kinks", e.target.value)} rows={3} className="gio-input w-full" /></Field>
            <Field label="Boundaries / limits"><textarea data-testid="fan-f-boundaries" value={form.boundaries} onChange={(e) => set("boundaries", e.target.value)} rows={3} className="gio-input w-full" /></Field>
          </div>

          <Field label="Notes">
            <textarea data-testid="fan-f-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} className="gio-input w-full" />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} data-testid="fan-form-cancel" className="btn-ghost">Cancel</button>
            <button onClick={onSave} data-testid="fan-form-save" className="btn-primary">Save fan</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--gio-muted)" }}>{label}</div>
      {children}
    </div>
  );
}
